import { ChangeDetectionStrategy, Component, inject, signal, WritableSignal } from '@angular/core';

import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import {
    catchError,
    combineLatest,
    forkJoin,
    map,
    Observable,
    of,
    switchMap,
    take,
    takeUntil,
    tap,
    withLatestFrom
} from 'rxjs';
import { ShDelayedActionService } from '../../../core/common/services/delayed-action.service';
import { ExtendedBaseProfileField } from '../../../core/profiles/entities/extended-profile-field.entity';
import { RecordId } from '../../../core/records/entities/metadata/metadata.entity';
import { PartialRecord } from '../../../core/records/entities/partial-record.class';
import { NotificationType } from '../../../elements/components/notification/entities/notification-types.entity';
import { ShFormGroupComponent } from '../../../form-controls/components/_core/form-group/form-group.component';
import { FormValue } from '../../../form-controls/entities/form-value.entity';
import { GenerateGuid } from '../../../helpers/guid-generator.helper';
import { ShBaseGenericInputFieldComponent } from '../../../profile-form/components/input/base-generic-input-field/base-generic-input-field.component';
import { ComplexListFieldValue } from '../../../profile-form/entities/field-values/field-type-values.entity';
import { FieldReadHelper } from '../../../profile-form/helpers/fields/field-read.helper';
import { ChildFieldToProfileField } from '../../../profile-form/helpers/to-profile-field/to-profile-field.helper';
import { ShToastFactory } from '../../../toasts/factories/toast.factory';
import { ShAbstractToastService } from '../../../toasts/services/toast.abstract.service';
import { Face, isFaceUpdated } from '../../entities/face.entity';
import { isNewPerson, isUnsetPerson, isUpdatePerson } from '../../entities/person.entity';
import { PersonDataService } from '../../services/person.data-service';
import { ShFormPersonSelectComponent } from '../form-person-select/form-person-select.component';

@Component({
    standalone: true,
    selector: 'sh-faces-list-input-field',
    templateUrl: './faces-list-input-field.component.html',
    styleUrls: ['./faces-list-input-field.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, TranslateModule, ShFormGroupComponent, ReactiveFormsModule, ShFormPersonSelectComponent]
})
export class ShFacesListInputFieldComponent extends ShBaseGenericInputFieldComponent {
    private readonly fieldHelper: FieldReadHelper = inject(FieldReadHelper);
    private readonly personDataService: PersonDataService = inject(PersonDataService);
    private readonly delayedActionService: ShDelayedActionService = inject(ShDelayedActionService);
    private readonly toastService: ShAbstractToastService = inject(ShAbstractToastService);
    private readonly toastFactory: ShToastFactory = inject(ShToastFactory);

    public childDefinitionLabel$: Observable<string>;
    public updatedFaceList: WritableSignal<Face[]> = signal([]);
    public isSupported: boolean = false;
    public recordId: RecordId;

    private readonly FACE_UPDATE_ID: string = GenerateGuid();
    private faceList: Face[];
    private createPersonList: Face[] = [];
    private updatePersonList: Face[] = [];
    private updatedFacesList: Face[] = [];
    private deleteFacesList: string[] = [];

    override init(profileField: ExtendedBaseProfileField, records: PartialRecord[], control: UntypedFormControl): void {
        // Only support single record editing
        this.isSupported = !this.isBatchEdit && records.length === 1;

        if (!this.isSupported) {
            return;
        }

        this.recordId = records[0].getRecordId();

        const values: FormValue<ComplexListFieldValue>[] = this.fieldHelper.getMergedComplexListFieldValue(
            profileField.FieldDefinition,
            records
        );

        this.initInputControls(values);
        this.initChildDefinitionStream();
    }

    private initInputControls(initialValues: FormValue<ComplexListFieldValue>[]): void {
        this.faceList = initialValues.map(formValue => formValue.value as unknown as Face);
        this.updatedFaceList.set(this.faceList);
    }

    private initChildDefinitionStream(): void {
        const childProfileField$ = this.profileField$.pipe(
            map(profileField => profileField.FieldDefinition.Children),
            map(children => {
                if (!children || children.length !== 1) {
                    throw new Error(`ComplexListField should have exactly 1 child, but has ${children?.length}`);
                }
                return children[0];
            }),
            withLatestFrom(this.profileField$),
            map(([childDef, profileField]) => ChildFieldToProfileField(childDef, profileField, this.isBatchEdit, false))
        );

        this.childDefinitionLabel$ = childProfileField$.pipe(
            map(childDefinition => childDefinition.FieldDefinition.Label)
        );
    }

    public updateFace(updatedFace: Face): void {
        const originalFace = this.faceList.find(originalFace => originalFace.Id === updatedFace.Id);

        if (!originalFace) {
            return;
        }

        this.ignorePreviousFaceChanges(updatedFace.Id);

        // Don't do anything if changes were reverted to original value
        const isUpdated = isFaceUpdated(originalFace, updatedFace);

        if (isUpdated) {
            const updatedPerson = updatedFace.Person;
            const createNewPerson = isNewPerson(updatedPerson);
            const nameChange = isUpdatePerson(updatedPerson);

            if (createNewPerson) {
                this.createPersonList.push(updatedFace);
            } else if (nameChange) {
                this.updatePersonList.push(updatedFace);
            } else {
                this.updatedFacesList.push(updatedFace);
            }
        }

        // Update display list
        this.updatedFaceList.update(faceList => {
            const faceIndex = faceList.findIndex(face => face.Id === updatedFace.Id);
            const newFaceList = [...faceList];
            newFaceList[faceIndex] = updatedFace;
            return newFaceList;
        });

        this.addUpdateFacesDelayedAction();
    }

    public deleteFace(faceId: string): void {
        this.ignorePreviousFaceChanges(faceId);

        this.deleteFacesList.push(faceId);

        // Update display list
        this.updatedFaceList.update(faceList => faceList.filter(face => face.Id !== faceId));

        this.addUpdateFacesDelayedAction();
    }

    private ignorePreviousFaceChanges(faceId: string): void {
        this.removePreviousDelayedActions(faceId);

        // Remove previous updates from lists
        this.createPersonList = this.createPersonList.filter(face => face.Id !== faceId);
        this.updatePersonList = this.updatePersonList.filter(face => face.Id !== faceId);
        this.updatedFacesList = this.updatedFacesList.filter(face => face.Id !== faceId);
        this.deleteFacesList = this.deleteFacesList.filter(id => id !== faceId);
    }

    // #region Create Person
    private createPersonCall(face: Face): Observable<Face | null> {
        const name = face.Person?.Name;

        if (!name) {
            return of(null);
        }

        return this.personDataService.createPerson(face.Id, name).pipe(
            take(1),
            takeUntil(this.destroy$),
            map(personApiFragment => ({
                ...face,
                Person: {
                    ...face.Person,
                    AiLinked: false,
                    RecordId: personApiFragment.getRecordId()
                }
            })),
            catchError(error => this.onCreatePersonError(error, name))
        );
    }

    private onCreatePersonError(httpError: HttpErrorResponse, name: string): Observable<null> {
        const message =
            httpError.error?.Code === 'ECONFL'
                ? this.translateService.instant('facial-recognition.toasts.create-person-conflict', { name })
                : this.translateService.instant('facial-recognition.toasts.create-person-error', { name });

        const toast = this.toastFactory.create({
            message,
            type: NotificationType.Error,
            icon: 'delete',
            iconRotate: false,
            autoClose: false,
            closable: true
        });

        this.toastService.addToast(toast);
        return of(null);
    }
    // #endregion

    // #region Update Person
    private updatePersonCall(face: Face): Observable<Face | null> {
        const recordId = face.Person?.RecordId;
        const name = face.Person?.Name;

        if (!recordId || !name) {
            return of(null);
        }

        return this.personDataService.updatePerson(face.Id, recordId, name).pipe(
            take(1),
            takeUntil(this.destroy$),
            map(() => ({
                ...face,
                Person: {
                    ...face.Person,
                    AiLinked: false
                }
            })),
            catchError(error => this.onUpdatePersonError(error, name))
        );
    }

    private onUpdatePersonError(httpError: HttpErrorResponse, name: string): Observable<null> {
        const message = this.translateService.instant('facial-recognition.toasts.update-person-error', { name });

        const toast = this.toastFactory.create({
            message,
            type: NotificationType.Error,
            icon: 'delete',
            iconRotate: false,
            autoClose: false,
            closable: true
        });

        this.toastService.addToast(toast);
        return of(null);
    }
    // #endregion

    // #region Update Faces
    private addUpdateFacesDelayedAction() {
        if (
            this.updatedFacesList.length === 0 &&
            this.createPersonList.length === 0 &&
            this.deleteFacesList.length === 0 &&
            this.updatePersonList.length === 0
        ) {
            return;
        }

        const updatePersonsStream$: Observable<Face[]> =
            this.updatePersonList.length > 0
                ? forkJoin(this.updatePersonList.map(face => this.updatePersonCall(face))).pipe(
                      map(updatedPersons => updatedPersons.filter(person => !!person))
                  )
                : of([]);

        const createPersonsStream$: Observable<Face[]> =
            this.createPersonList.length > 0
                ? forkJoin(this.createPersonList.map(face => this.createPersonCall(face))).pipe(
                      map(newPersons => newPersons.filter(person => !!person))
                  )
                : of([]);

        // First wait until all people are created and updated.
        // Then add these people to the list of updated faces to immediately add them to the current record
        const personsUpdateStream$ = of(null).pipe(
            tap(() => this.createUpdateFacesWarningToast()),
            switchMap(() => combineLatest([createPersonsStream$, updatePersonsStream$])),
            switchMap(([createdPersons, updatedPersons]) => this.updateFaces(createdPersons, updatedPersons))
        );
        this.delayedActionService.addDelayedAction(this.FACE_UPDATE_ID, personsUpdateStream$);
    }

    private updateFaces(createdPersons: Face[], updatedPersons: Face[]): Observable<PartialRecord> {
        return this.personDataService
            .updateFaces(
                this.recordId,
                [...this.updatedFacesList, ...createdPersons, ...updatedPersons],
                this.deleteFacesList
            )
            .pipe(
                take(1),
                takeUntil(this.destroy$),
                catchError(error => this.onUpdateFacesError(error))
            );
    }

    private createUpdateFacesWarningToast(): void {
        const setPersonsList = this.updatedFacesList.filter(face => !isUnsetPerson(face.Person));

        const amount = setPersonsList.length + this.createPersonList.length + this.updatePersonList.length;

        if (amount === 0) {
            return;
        }

        const toast = this.toastFactory.create({
            message: this.translateService.translate('facial-recognition.toasts.update-person-warning', { amount }),
            type: NotificationType.Warning,
            icon: 'warning',
            iconRotate: false,
            autoClose: true,
            closable: true
        });

        this.toastService.addToast(toast);
    }

    private onUpdateFacesError(httpError: HttpErrorResponse): Observable<never> {
        const message = this.translateService.instant('facial-recognition.toasts.update-faces-error');

        const toast = this.toastFactory.create({
            message,
            type: NotificationType.Error,
            icon: 'delete',
            iconRotate: false,
            autoClose: true,
            closable: true
        });

        this.toastService.addToast(toast);
        throw new Error('Failed to save faces');
    }
    // #endregion

    private removePreviousDelayedActions(faceId: string): void {
        // Remove Create person actions
        this.delayedActionService.removeDelayedAction(faceId);

        // Remove Update persons action
        this.delayedActionService.removeDelayedAction(this.FACE_UPDATE_ID);
    }
}
